import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type State =
  | { kind: "loading" }
  | { kind: "valid" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "done" }
  | { kind: "error"; message: string };

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : "",
  }),
});

function UnsubscribePage() {
  const { token } = useSearch({ from: "/unsubscribe" });
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) return setState({ kind: "invalid" });
    (async () => {
      try {
        const r = await fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`);
        const j = await r.json();
        if (j.valid) setState({ kind: "valid" });
        else if (j.reason === "already_unsubscribed") setState({ kind: "already" });
        else setState({ kind: "invalid" });
      } catch (e: any) {
        setState({ kind: "error", message: e?.message ?? "Network error" });
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState({ kind: "loading" });
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const j = await r.json();
      if (j.success) setState({ kind: "done" });
      else if (j.reason === "already_unsubscribed") setState({ kind: "already" });
      else setState({ kind: "error", message: j.error ?? "Could not unsubscribe" });
    } catch (e: any) {
      setState({ kind: "error", message: e?.message ?? "Network error" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 grid place-items-center px-6 py-16">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl text-center">
        <div className="text-5xl mb-3">💌</div>
        <h1 className="text-2xl font-bold mb-2">Email preferences</h1>

        {state.kind === "loading" && <p className="text-muted-foreground">Just a moment…</p>}

        {state.kind === "valid" && (
          <>
            <p className="text-muted-foreground mb-6">
              You can stop receiving emails from Jozori at this address. You'll
              still be able to use the app — only emails will pause.
            </p>
            <button
              onClick={confirm}
              className="w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
            >
              Confirm unsubscribe
            </button>
          </>
        )}

        {state.kind === "already" && (
          <p className="text-muted-foreground">You're already unsubscribed. 💛</p>
        )}

        {state.kind === "done" && (
          <p className="text-muted-foreground">
            Done — you won't receive these emails anymore. We'll miss you 🌱
          </p>
        )}

        {state.kind === "invalid" && (
          <p className="text-muted-foreground">
            This unsubscribe link is invalid or has expired.
          </p>
        )}

        {state.kind === "error" && (
          <p className="text-destructive text-sm">{state.message}</p>
        )}
      </div>
    </div>
  );
}
